/**
 * Which sentence the Materiały block prints, which controls it offers, and where a save goes.
 *
 * Three facts meet here and nowhere else: whose exercise it is, whether the trainer has an overlay
 * on it, and whether that second question has been answered yet. A component working it out from
 * an ownership, a query status and a nullable array would get it wrong the first time an overlay
 * read 403s — which it does, by design, on every exercise the trainer owns.
 *
 * ── The three sentences ─────────────────────────────────────────────────────────────────────
 * · **`inherited`** — a library movement showing the library's own links. `GET /exercise/{id}/overlay`
 *   answered with `links: null`. „To są linki z biblioteki. Jeśli je zmienisz, Twoi klienci
 *   zobaczą Twoją wersję."
 * · **`overridden`** — a library movement this trainer has overlaid, which is what
 *   „Przywróć wersję z biblioteki" undoes. The overlay answered with a list, or with `[]` — the
 *   trainer overrode the library's links to NONE, which is an override like any other and is the
 *   one case a resolved empty array cannot tell from an inherited empty one.
 * · **`own`** — the trainer's own movement. The overlay endpoint refuses it with 403 and the links
 *   live on the row itself, so there is nothing to inherit and nothing to revert to.
 *
 * ── Which of `null`, `[]` and a list means what ─────────────────────────────────────────────
 * The overlay read is the only thing that carries the distinction. `GET /exercise`'s resolved
 * `links` are what the trainer SEES with the two collapsed into one array, and no amount of looking
 * at it recovers which it was. That is the whole reason the second read exists, and the whole
 * reason „Przywróć" can only be offered off it.
 *
 * ── A fourth state, because the answer can be missing ───────────────────────────────────────
 * The overlay read is open, or it failed with something that is not a 403. The block still draws
 * the links — they are on the catalogue's own row and never waited for anything — but it cannot
 * say whose they are, so it does not claim to. Mobile already words that case
 * (`exerciseLibrary.links.overlayLoadErrorBody`) and says the honest thing: saving now creates or
 * overwrites your version.
 *
 * **Nothing here translates.** Keys out, resolved by `ui/` — `model/` may not import i18next, and
 * every key below is already in one of the two locale files.
 */

import { MAX_EXERCISE_LINKS, type ExerciseOwnership } from '~/entities/exercise-library';

/** What the overlay read has said so far. */
export type OverlayReading =
  /** Still open. */
  | 'pending'
  /** `links: null` — the library's own links are showing. */
  | 'inherited'
  /** A list, or `[]` — this trainer replaced them. */
  | 'overridden'
  /** 403. The exercise is the trainer's own and the question does not apply. */
  | 'notApplicable'
  /** Anything else went wrong. */
  | 'failed';

export interface MaterialsStatusReading {
  readonly ownership: ExerciseOwnership;
  readonly overlay: OverlayReading;
  /** How many materials are on screen — the cap is counted against what the trainer can see. */
  readonly linkCount: number;
}

export type MaterialsStatusKind = 'inherited' | 'overridden' | 'own' | 'unknown';

export interface MaterialsStatusView {
  readonly kind: MaterialsStatusKind;
  /**
   * The sentence under the heading, as a KEY. `null` only while the overlay read is still open —
   * the block reserves the line either way, so nothing moves when it lands.
   */
  readonly noteKey: string | null;
  /** „Dodaj link". */
  readonly canAdd: boolean;
  /** „Przywróć wersję z biblioteki" — only ever offered on an overlay that is known to exist. */
  readonly canRevert: boolean;
  /**
   * Which endpoint a save takes.
   *
   * `overlay` is `PUT /exercise/{id}/overlay/links`; `exercise` is `PATCH /exercise/{id}`, because
   * an overlay on your own movement is refused. It follows OWNERSHIP alone and never the overlay
   * read: a trainer whose overlay read failed on a library movement still saves through the
   * overlay, which is what the error sentence above tells them.
   */
  readonly route: 'overlay' | 'exercise';
}

/**
 * The three facts, resolved into one answer.
 *
 * Ownership is asked FIRST and the overlay reading cannot override it: `own` is decided from the
 * owner id, and a stale `isCustomized` or a 403 arriving late must not turn the trainer's own
 * movement into a library one with a „Przywróć" beside it.
 */
export function materialsStatus(reading: MaterialsStatusReading): MaterialsStatusView {
  const canAdd = reading.linkCount < MAX_EXERCISE_LINKS;

  if (reading.ownership === 'own') {
    return {
      kind: 'own',
      noteKey: 'web:exerciseLibrary.materials.ownNote',
      canAdd,
      canRevert: false,
      route: 'exercise',
    };
  }

  switch (reading.overlay) {
    case 'overridden':
      return {
        kind: 'overridden',
        noteKey: 'exerciseLibrary.links.overriddenNote',
        canAdd,
        canRevert: true,
        route: 'overlay',
      };
    case 'inherited':
      return {
        kind: 'inherited',
        noteKey: 'exerciseLibrary.links.inheritedNote',
        canAdd,
        canRevert: false,
        route: 'overlay',
      };
    // A 403 on a movement the trainer does NOT own is not the designed refusal — it is the server
    // saying no to a question this screen believes it may ask — so it is read as the failure it is
    // rather than silently as "no overlay".
    case 'notApplicable':
    case 'failed':
      return {
        kind: 'unknown',
        noteKey: 'exerciseLibrary.links.overlayLoadErrorBody',
        canAdd,
        canRevert: false,
        route: 'overlay',
      };
    case 'pending':
      return { kind: 'unknown', noteKey: null, canAdd, canRevert: false, route: 'overlay' };
  }
}
